import angular from 'angular'
import debounce from 'lodash/debounce'
import ngRedux from 'ng-redux'
import toArray from 'lodash/toArray'
import intersection from 'lodash/intersection'
import { vn } from 'vend-number'
import { ERRORS, PERMISSIONS } from '../../constants'
import template from '../../../templates/sale/line-item.html'
import { $document, $rootScope, $timeout } from '../../ng-compat/imports'
import { LineItemUser } from '../../models/line-item'
import { LoggedInUserChangeEvent } from '../../managers/user-manager'
import { isGiftCard } from '../../functions/lineItem'
import { FulfilmentType } from '../../models/fulfilment'
import { LineItemTab } from '../../components/sale/sale-line-item/LineItemTabs'
import {
  isLayby,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isNonFulfillmentOnAccountSale,
} from '../../functions/sale'
import {
  selectLineItemCurrentTab,
  selectExpandedLineItemId,
  setLineItemCurrentTab,
  openFulfilmentLineItemPopover,
  removeLineItemQuantityInputError,
  setLineItemQuantityInputError,
} from '../../state/slices/uiStateSlice'
import { hasServiceOrder } from '../../functions/service'
import {
  selectCurrentSale,
  selectHasCompletedFulfilments,
  selectSaleId,
  selectSaleLocalId,
  selectSaleStatus,
  selectSaleUser,
  updateLineItem,
} from '../../state/slices/currentSale/currentSaleSlice'
import { User } from '../../models/sale.constants'
import { SaleManager } from '../../managers/sale-manager'
import { RegisterManager } from '../../managers/register-manager'
import { RootState } from '../../state/store'

const ACTIONS_CONTAINER_CLASS = '.sale-list .li-cell-actions-container'
const ACTIONS_CONTAINER_EXPANDED_CLASS = 'li-cell-actions-container--expanded'
const SPACE = 32
const ENTER = 13

/**
 * Represents a line item element.
 *
 * @module webregister.directives.lineItem
 */
angular.module('webregister.directives').directive('lineItem', function () {
  return {
    require: '^form',
    scope: {
      lineItem: '=',
    },
    restrict: 'E',
    replace: true,
    template,
    controllerAs: 'lineItemCtrl',
    bindToController: true,

    /**
     * =======================================
     * LINE ITEM LINK
     * =======================================
     */
    link(scope: $TSFixMe, $element, attrs, formCtrl) {
      // Ensures formCtrl available via scope in controller
      scope.formCtrl = formCtrl
      scope.lineItemCtrl.$element = $element
    },

    /*
     * =======================================
     * LINE ITEM CONTROLLER
     * =======================================
     */
    controller(
      this: $TSFixMe,
      $scope,
      $element,
      productModals,
      userManager,
      posStateManager,
      vdTracking,
      vdFeatureManager,
      saleInventoryData,
      saleManager: SaleManager,
      registerManager: RegisterManager,
      $ngRedux: ngRedux.INgRedux
    ) {
      const outlet = registerManager.getActiveOutlet()
      this.saleInventoryData = saleInventoryData
      this.outletTimeZone = outlet && outlet.time_zone
      this.loggedInUser = posStateManager.getUser()
      this.isUserSwitched = false
      this.currentTab = 'detail'
      this.$ngRedux = $ngRedux

      const inputErrors = {
        discount: null,
        price: null,
        quantity: null,
      }
      let lineItemIsReturn: $TSFixMe
      let canFocusOnExpand: $TSFixMe
      let lineItemIsRemoved = false
      this.$onInit = () => {
        lineItemIsReturn = this.lineItem.isReturn
        // Only allow line item auto focus if the item is not (on-account / layby) / confirmed
        canFocusOnExpand = !this.lineItem.confirmed
        this.showSalePerson = vdFeatureManager.get('sales_person_attribution')
        userManager.on('loggedInUserChange', this.onLoggedInUserChange)

        $scope.$on('$destroy', () => {
          userManager.off('loggedInUserChange', this.onLoggedInUserChange)
        })
      }

      this.$postLink = () => {
        if (this.isFulfillmentLifFeatureEnabled()) {
          const expandedLineItemId = selectExpandedLineItemId(
            this.$ngRedux.getState()
          )
          if (expandedLineItemId === this.lineItem.localId) {
            scrollToLineItem()
          }
        }
      }

      /**
       * Returns true if loggedInUserChange is called with a different logged in user or false otherwise
       *
       * @method hasUserSwitched
       * @return {Boolean}
       *
       *
       */
      this.hasUserSwitched = () => {
        return this.isUserSwitched
      }

      /**
       * Handle the change in logged in user
       */
      this.onLoggedInUserChange = (event: LoggedInUserChangeEvent) => {
        this.isUserSwitched = this.loggedInUser.id !== event.loggedInUser.id
        this.loggedInUser = event.loggedInUser
      }

      /**
       * Handle removing a line item.
       *
       * @method remove
       */
      this.remove = () => {
        for (const input of Object.keys(inputErrors)) {
          const inputError = inputErrors[input as keyof typeof inputErrors]
          if (inputError) {
            $scope.formCtrl.$setValidity(inputError, true)
          }
        }
        lineItemIsRemoved = true
        $scope.$emit('sale:remove-line-item', this.lineItem)
      }

      /**
       * Determine if any of the line item form inputs are invalid.
       *
       * @method hasInvalidInput
       *
       * @return {Boolean} True if either quantity, discount percent or display price are invalid, false otherwise.
       */
      this.hasInvalidInput = () => {
        if (!$scope.formCtrl) {
          // A React component can call into this method before link is called. Handle that possibility.
          return false
        }
        const hasInvalidInput =
          inputErrors.discount || inputErrors.price || inputErrors.quantity

        return hasInvalidInput
      }

      /**
       * Determine if the sale is marked as unfulfilled
       *
       * @method isMarkedAsUnfulfilled
       *
       * @return {Boolean} True if sale is marked as unfulfilled
       */
      this.isMarkedAsUnfulfilled = () => {
        if (vdFeatureManager.get('fulfillment_lif')) {
          return (
            this.lineItem.fulfilmentType === FulfilmentType.DELIVERY ||
            this.lineItem.fulfilmentType === FulfilmentType.PICKUP
          )
        }
        return isMarkedAsUnfulfilled(selectSaleStatus($ngRedux.getState()))
      }

      /**
       * Determine if the line item is allowed to be edited
       *
       * @method editAllowedForConfirmed
       *
       * @returns {Boolean} True if the line item is allowed to be edited
       */
      this.editAllowedForConfirmed = () => {
        const isIndependentServiceLineItemsEnabled = vdFeatureManager.get(
          'independent_service_line_items'
        )
        const currentSale = selectCurrentSale($ngRedux.getState())

        return (
          isLegacyServiceSale(currentSale.status) ||
          (isLayby(currentSale.status) &&
            hasServiceOrder(
              currentSale.serviceFields,
              currentSale.customFields
            ) &&
            !isIndependentServiceLineItemsEnabled)
        )
      }

      /**
       * Asserts whether the active user has entered a valid quantity for a return i.e. not more than original quantity sold.
       *
       * @method isInvalidMinReturnQuantity
       *
       * @return {Boolean} True if the entered quantity is invalid, false otherwise.
       */
      this.isInvalidMinReturnQuantity = function () {
        return (
          this.hasReturnPermission() &&
          !this.isMarkedAsUnfulfilled() &&
          inputErrors.quantity === ERRORS.INVALID_MIN_RETURN_QUANTITY
        )
      }

      /**
       * Asserts whether the active user has entered a valid quantity for a return i.e. not greater than 0.
       *
       * @method isInvalidMaxReturnQuantity
       *
       * @return {Boolean} True if the entered quantity is invalid, false otherwise.
       */
      this.isInvalidMaxReturnQuantity = function () {
        return (
          this.hasReturnPermission() &&
          !this.isMarkedAsUnfulfilled() &&
          inputErrors.quantity === ERRORS.INVALID_MAX_RETURN_QUANTITY
        )
      }

      /**
       * Asserts whether the active user has entered a valid quantity.
       *
       * @method isInvalidMinQuantity
       *
       * @return {Boolean} True if the entered quantity is invalid, false otherwise
       */
      this.isInvalidMinQuantity = function () {
        if (this.lineItem.confirmed) {
          return false
        }

        return inputErrors.quantity === ERRORS.INVALID_MIN
      }

      this.isInvalidDigitLength = function () {
        return (
          inputErrors.quantity === ERRORS.INVALID_MAX ||
          (inputErrors.quantity === ERRORS.INVALID_MIN &&
            this.hasReturnPermission() &&
            !this.isMarkedAsUnfulfilled())
        )
      }

      /**
       * Asserts whether the active user has permission to make a return.
       *
       * @method hasReturnPermission
       *
       * @return {Boolean} True if the user has the permission to make a return
       */
      this.hasReturnPermission = function () {
        return userManager.hasPermission(PERMISSIONS.SALE_RETURN)
      }

      /**
       * Function for binding error message visibility when this line item is below minimum stock.
       *
       * @return {Boolean} True if the quantity is invalid due to low stock
       */
      this.isLowStock = function () {
        return inputErrors.quantity === ERRORS.NO_INVENTORY
      }

      /**
       * Check if the feature is enabled
       * @returns {Boolean} True if the feature is enabled
       */
      this.isLifB3Enabled = (): boolean =>
        vdFeatureManager.get('fulfillment_lif_b3')

      /**
       * Check if there is some fulfilmentCompleted for the line items
       */
      this.hasFulfilmentsCompleted = (): boolean =>
        selectHasCompletedFulfilments($ngRedux.getState<RootState>())

      /**
       * Get the inventory level for the current line item
       *
       * @method getInventoryLevel
       *
       * @return {Number} The inventory level for the current line item
       */
      this.getInventoryLevel = () => {
        const inventoryData = this.saleInventoryData.getInventoryData(
          this.lineItem
        )

        return inventoryData.inventoryLevel
      }

      /**
       * Disable validation on inventory level so the user can still proceed with the sale
       *
       * @method continueWithLowStock
       */
      this.continueWithLowStock = function () {
        this.saleInventoryData.disableInventoryValidation(this.lineItem)
      }

      /**
       * Open the fulfilment popover for the line item
       *
       * @method opendFulfilmentPopover
       */
      this.openFulfilmentPopover = () =>
        this.$ngRedux.dispatch(
          openFulfilmentLineItemPopover(this.lineItem.localId)
        )

      /**
       * Show product information.
       *
       * @method showInfo
       */
      this.showInfo = function () {
        vdTracking.trackUserEvent({
          eventName: 'Show Product Information',
          metadata: {
            view: 'Line Item',
          },
        })
        productModals.viewDetails(this.lineItem.product).then(() => {
          $rootScope.$emit('autocomplete:global-search:focus')
        })
      }

      /**
       * Makes the line item accessible by keyboard by triggering the expansion when pressing space or enter
       *
       * @param {jQuery Event} event
       *        The keypress event triggered on the line item
       */
      this.handleLineItemKeypress = (event: $TSFixMe) => {
        if (event.which === ENTER || event.which === SPACE) {
          this.toggleLineItem(event)
          // Space triggers a scroll which we have to prevent
          event.preventDefault()
        }
      }

      /**
       * Handle tab change
       *
       * @param {String} event
       *       The tab change event see `sale-line-item/LineItemTabs.tsx` for possible values
       */
      this.handleTabChange = (event: LineItemTab) => {
        if (this.isFulfillmentLifFeatureEnabled()) {
          this.$ngRedux.dispatch(setLineItemCurrentTab(event))
        } else {
          $scope.$apply(() => {
            this.currentTab = event
          })
        }
      }

      this.getCurrentTab = () => {
        if (this.isFulfillmentLifFeatureEnabled()) {
          return selectLineItemCurrentTab(this.$ngRedux.getState())
        } else {
          return this.currentTab
        }
      }

      this.shouldBeExpanded = () => {
        if (this.isFulfillmentLifFeatureEnabled()) {
          return (
            selectExpandedLineItemId(this.$ngRedux.getState()) ===
            this.lineItem.localId
          )
        }
        return false
      }

      /**
       * Scroll the view to this line item if it is out of view
       */
      const scrollToLineItem = async () => {
        // Get the sale body node
        const saleBodyNode = ($document[0] as Document).querySelector(
          '.sale-body'
        )

        // Perform the scroll operation
        if (saleBodyNode) {
          await waitUntilFullyRendered(saleBodyNode)
          scrollToElementIfOutOfView(saleBodyNode, this.$element[0])
        }
      }

      /**
       * Handle expanding / collapsing of extra line item actions. Ensure only one line item is expanded at
       * once and prevent collapse when invalid value has been entered.
       *
       * @todo naming of this "extra" section is a bit yuck.
       *
       * @method toggleLineItem
       */
      this.toggleLineItem = ($event: $TSFixMe) => {
        // Reset tab to detail, no validation happens on fulfilment tab
        this.handleTabChange(LineItemTab.DETAIL)
        // Gift cards cannot be expanded
        if (isGiftCard(this.lineItem.product)) {
          return
        }

        if (!this.isFulfillmentLifFeatureEnabled()) {
          const document = $document[0] as Document
          const allLineItemActionsNodes = angular.element(
            document.querySelectorAll(ACTIONS_CONTAINER_CLASS)
          )
          const allLineItemCellNodes = angular.element(
            document.querySelectorAll('.sale-list .li-cell')
          )
          const allLineItemChevronNodes = angular.element(
            document.querySelectorAll('.sale-list .wr-expandable-chevron')
          )
          const lineItemActionsNode = angular.element(
            $element[0].querySelector(ACTIONS_CONTAINER_CLASS)!
          )
          const lineItemCellNode = angular.element(
            $element[0].parentNode.querySelector('.li-cell')
          )
          const lineItemChevronNode = angular.element(
            $element[0].querySelector('.wr-expandable-chevron')
          )
          // If the line item is expanded, then collapse it
          if (lineItemActionsNode.hasClass(ACTIONS_CONTAINER_EXPANDED_CLASS)) {
            // Collapse
            lineItemActionsNode.removeClass(ACTIONS_CONTAINER_EXPANDED_CLASS)
            lineItemCellNode.removeClass('li-cell--expanded')

            // Reset chevron on collapse
            lineItemChevronNode.removeClass('fa-angle-down')
            lineItemChevronNode.addClass('fa-angle-right')
          } else {
            // Expand and ensure only one line item expanded at a time (collapse any other open line items)
            allLineItemActionsNodes.removeClass(
              ACTIONS_CONTAINER_EXPANDED_CLASS
            )
            allLineItemCellNodes.removeClass('li-cell--expanded')

            // Reset chevron for all line item nodes
            allLineItemChevronNodes.removeClass('fa-angle-down')
            allLineItemChevronNodes.addClass('fa-angle-right')

            // Set chevron angle down for expanded line item
            lineItemChevronNode.removeClass('fa-angle-right')
            lineItemChevronNode.addClass('fa-angle-down')

            lineItemActionsNode.addClass(ACTIONS_CONTAINER_EXPANDED_CLASS)
            lineItemCellNode.addClass('li-cell--expanded')
          }
        }

        if (canFocusOnExpand) {
          const priceFieldClasses = [
            'li-cell-summary--totals-container',
            'wr-li-cell-summary-price-value',
            'li-cell-summary--total-base-price',
          ]
          const clickedElementClassList = toArray($event.target.classList)

          // Broadcast down to child scopes to inform them the line item has expanded. Enables things like focusing the
          // quantity / price fields when the line item is expanded. Wait 200ms to let the expand animation complete.
          const isPriceSelectable =
            !lineItemIsReturn &&
            this._canApplyDiscount() &&
            intersection(priceFieldClasses, clickedElementClassList).length > 0
          const focusRef = (ref: $TSFixMe) => {
            if (ref) {
              ref.focus()
              ref.setSelectionRange(0, ref.value.length)
            }
          }
          const broadcaster = isPriceSelectable
            ? () => focusRef(priceInputRef)
            : () => focusRef(quantityInputRef)

          $timeout(broadcaster, 200)
        }
      }

      const inputErrorHandler = (input: keyof typeof inputErrors) => (
        error: $TSFixMe
      ) => {
        $scope.$applyAsync(() => {
          if (lineItemIsRemoved) {
            return // exit early
          }
          if (inputErrors[input]) {
            $scope.formCtrl.$setValidity(inputErrors[input], true)
          }
          if (error) {
            $scope.formCtrl.$setValidity(error, false)
          }
          inputErrors[input] = error
        })
      }

      this.onDiscountError = (error: string) => {
        inputErrorHandler('price')(null)
        inputErrorHandler('discount')(error)
      }

      this.onPriceError = (error: string) => {
        inputErrorHandler('discount')(null)
        inputErrorHandler('price')(error)
      }

      this.onQuantityError = (error: string) => {
        if (this.isLifB3Enabled()) {
          this.$ngRedux.dispatch(
            lineItemIsRemoved
              ? removeLineItemQuantityInputError(this.lineItem.localId)
              : setLineItemQuantityInputError({
                  id: this.lineItem.localId,
                  error,
                })
          )
        }
        inputErrorHandler('quantity')(error)
      }

      let priceInputRef: $TSFixMe
      this.setPriceRef = (ref: $TSFixMe) => {
        priceInputRef = ref
      }

      let quantityInputRef: $TSFixMe
      this.setQuantityRef = (ref: $TSFixMe) => {
        quantityInputRef = ref
      }

      const saveSale = debounce(() => saleManager.saveSale(), 300)

      /**
       * Save the sale when the line item note changes.
       *
       * @param {String} note
       */
      this.onNoteChange = (note: string) => {
        $scope.$apply(() => {
          $ngRedux.dispatch(updateLineItem({ ...this.lineItem, note }))
          saveSale()
        })
      }

      /**
       * Flush stored-up note debounces on note blur so that the note is saved immediately
       * when the field loses focus.
       */
      this.onNoteBlur = () => {
        saveSale.flush()
      }

      /**
       * Save the sale and track UBE when the sales person changes.
       *
       * @param {LineItemUser} user
       */
      this.onSalesPersonSelect = (user: LineItemUser) => {
        // Fire the UBE if the user has been changed
        vdTracking.trackUserEvent({
          eventName: 'sale.line_item.salesperson.update',
          metadata: {
            saleId:
              selectSaleId($ngRedux.getState()) ||
              selectSaleLocalId($ngRedux.getState()),
            userId: user.id,
            lineItemId: this.lineItem.id || this.lineItem.localId,
          },
        })

        $scope.$apply(() => {
          $ngRedux.dispatch(
            updateLineItem({ ...this.lineItem, salesperson: user })
          )
          saveSale()
        })
      }

      /**
       * Used to determine if the current user can apply discounts to line items
       *
       * @method canApplyDiscount
       *
       * @return {Boolean} True if the user should be allowed to apply to line items, false otherwise
       */
      this._canApplyDiscount = () => userManager.canApplyLineItemDiscount()

      /**
       * Returns the user created the sale if available
       */
      this.getSaleUser = (): User | null => {
        return selectSaleUser($ngRedux.getState()) || null
      }

      /**
       * Function for binding error message visibility when the discount is above maximum.
       *
       * @method isInvalidMaxDiscountPercent
       *
       * @return {Boolean} True if the maximum discount is exceeded
       */
      this.isInvalidMaxDiscountPercent = function () {
        return inputErrors.discount === ERRORS.INVALID_DISCOUNT
      }

      /**
       * Function for binding error message visibility when the discount price is below minimum.
       *
       * @method isInvalidMaxDiscountPrice
       *
       * @return {Boolean} True if the maximum price discount is exceeded
       */
      this.isInvalidMaxDiscountPrice = function () {
        return inputErrors.price === ERRORS.INVALID_DISCOUNT
      }

      /**
       * Check if the specified field has an error
       *
       * @param {field} one of the line item input fields
       */
      this.getFieldError = (field: 'price' | 'discount'): string | null => {
        return inputErrors[field]
      }

      /**
       * Check if the line item has been marked up
       */
      this.isPriceMarkedUp = () => {
        return vn(this.lineItem.displayMarkupPercent).gt(0)
      }

      this.isFulfillmentLifFeatureEnabled = () => {
        return vdFeatureManager.get('fulfillment_lif')
      }

      this.shouldShowContinueAnywayOptionWarningLabel = () => {
        const reduxCurrentSale = selectCurrentSale($ngRedux.getState())
        return (
          !this.isLifB3Enabled() ||
          isNonFulfillmentOnAccountSale(reduxCurrentSale) ||
          this.hasFulfilmentsCompleted()
        )
      }

      this.shouldShowFulfillmentPopoverOptionWarningLabel = () => {
        return !this.shouldShowContinueAnywayOptionWarningLabel()
      }
    },
  }
})

/**
 * Scroll the inner element into view if it is outside the boundaries of the outer element
 *
 * @param {Element} outerElement
 * @param {Element} innerElement
 */
export const scrollToElementIfOutOfView = (
  outerElement: Element,
  innerElement: Element
) => {
  // Check if the element is out of view
  const outerElementNodeRect = outerElement.getBoundingClientRect()
  const innerElementNodeRect = innerElement.getBoundingClientRect()

  const isOutOfView =
    innerElementNodeRect.top < outerElementNodeRect.top ||
    innerElementNodeRect.bottom > outerElementNodeRect.bottom

  // Scroll the element into view if it is out of view
  if (isOutOfView) {
    innerElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }
}

/**
 * Wait until all elements under the given element is fully rendered before calling the callback function
 *
 * @param {Element} element
 * @param callback
 */
export const waitUntilFullyRendered = (
  element: Element,
  observationTimeout: number = 50 // Default observation timeout of 50 ms
): Promise<void> => {
  return new Promise(resolve => {
    let timeout: NodeJS.Timeout

    // Use MutationObserver to ensure the element is fully re-rendered before resolving the promise
    const observer = new MutationObserver((_, obs) => {
      // Clear any existing timeout to debounce
      if (timeout) {
        clearTimeout(timeout)
      }

      // Set a timeout to resolve the promise after a short delay
      // This will allow everything inside the element to be rendered first
      timeout = setTimeout(() => {
        // Disconnect the observer when done
        obs.disconnect()

        // Resolve the promise
        resolve()
      }, observationTimeout)
    })

    // Start observing the element for changes
    observer.observe(element, {
      childList: true,
      subtree: true,
    })

    // Set a timeout to stop observing after the specified duration
    timeout = setTimeout(() => {
      observer.disconnect()
      resolve()
    }, observationTimeout)
  })
}
